import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

// Language translations
const translations = {
  en: {
    // Navigation
    'nav.overview': 'Overview',
    'nav.calls': 'Calls',
    'nav.analytics': 'Analytics',
    'nav.orders': 'Orders',
    'nav.users': 'Users',
    'nav.monitoring': 'Monitoring',
    'nav.billing': 'Billing',
    'nav.settings': 'Settings',

    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.view': 'View',
    'common.refresh': 'Refresh',
    'common.export': 'Export',
    'common.download': 'Download',
    'common.search': 'Search',
    'common.filter': 'Filter',

    // Dashboard
    'dashboard.title': 'Dashboard Overview',
    'dashboard.subtitle': 'Real-time insights for your AI voice platform',
    'dashboard.totalCalls': 'Total Calls',
    'dashboard.activeCalls': 'Active Calls',
    'dashboard.avgDuration': 'Avg Duration',
    'dashboard.revenue': 'Revenue',
    'dashboard.activeUsers': 'Active Users',
    'dashboard.systemUptime': 'System Uptime',
    'dashboard.alerts': 'Active Alerts',

    // Calls
    'calls.title': 'Call Management',
    'calls.subtitle': 'Monitor and manage all voice calls in real-time',
    'calls.status': 'Status',
    'calls.duration': 'Duration',
    'calls.started': 'Started',
    'calls.actions': 'Actions',
    'calls.callDetails': 'Call Details',
    'calls.transcript': 'Transcript',
    'calls.recording': 'Recording',

    // Auth
    'auth.login': 'Sign In',
    'auth.register': 'Sign Up',
    'auth.email': 'Email Address',
    'auth.passcode': 'Passcode',
    'auth.firstName': 'First Name',
    'auth.lastName': 'Last Name',
    'auth.companyName': 'Company Name',
    'auth.signIn': 'Sign In',
    'auth.signUp': 'Sign Up',
    'auth.dontHaveAccount': "Don't have an account?",
    'auth.alreadyHaveAccount': 'Already have an account?',

    // Settings
    'settings.title': 'Settings',
    'settings.subtitle': 'Manage your account, company, and platform settings',
    'settings.profile': 'Profile',
    'settings.company': 'Company',
    'settings.voice': 'Voice & AI',
    'settings.api': 'API & Webhooks',
    'settings.notifications': 'Notifications',

    // Billing
    'billing.title': 'Billing & Usage',
    'billing.subtitle': 'Manage your subscription and view usage analytics',
    'billing.currentPlan': 'Current Plan',
    'billing.minutesUsed': 'Minutes Used',
    'billing.totalCalls': 'Total Calls',
    'billing.currentCharges': 'Current Charges',

    // Analytics
    'analytics.title': 'Analytics Dashboard',
    'analytics.subtitle': 'Comprehensive insights and performance metrics'
  },

  es: {
    // Navigation
    'nav.overview': 'Resumen',
    'nav.calls': 'Llamadas',
    'nav.analytics': 'Análisis',
    'nav.orders': 'Pedidos',
    'nav.users': 'Usuarios',
    'nav.monitoring': 'Monitoreo',
    'nav.billing': 'Facturación',
    'nav.settings': 'Configuración',

    // Common
    'common.loading': 'Cargando...',
    'common.error': 'Error',
    'common.success': 'Éxito',
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.view': 'Ver',
    'common.refresh': 'Actualizar',
    'common.export': 'Exportar',
    'common.download': 'Descargar',
    'common.search': 'Buscar',
    'common.filter': 'Filtrar',

    // Dashboard
    'dashboard.title': 'Panel de Control',
    'dashboard.subtitle': 'Información en tiempo real para tu plataforma de voz IA',
    'dashboard.totalCalls': 'Total de Llamadas',
    'dashboard.activeCalls': 'Llamadas Activas',
    'dashboard.avgDuration': 'Duración Promedio',
    'dashboard.revenue': 'Ingresos',
    'dashboard.activeUsers': 'Usuarios Activos',
    'dashboard.systemUptime': 'Tiempo de Actividad',
    'dashboard.alerts': 'Alertas Activas',

    // Auth
    'auth.login': 'Iniciar Sesión',
    'auth.register': 'Registrarse',
    'auth.email': 'Dirección de Email',
    'auth.passcode': 'Código de Acceso',
    'auth.firstName': 'Nombre',
    'auth.lastName': 'Apellido',
    'auth.companyName': 'Nombre de la Empresa',
    'auth.signIn': 'Iniciar Sesión',
    'auth.signUp': 'Registrarse',
    'auth.dontHaveAccount': '¿No tienes una cuenta?',
    'auth.alreadyHaveAccount': '¿Ya tienes una cuenta?'
  },

  fr: {
    // Navigation
    'nav.overview': 'Aperçu',
    'nav.calls': 'Appels',
    'nav.analytics': 'Analytique',
    'nav.orders': 'Commandes',
    'nav.users': 'Utilisateurs',
    'nav.monitoring': 'Surveillance',
    'nav.billing': 'Facturation',
    'nav.settings': 'Paramètres',

    // Common
    'common.loading': 'Chargement...',
    'common.error': 'Erreur',
    'common.success': 'Succès',
    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
    'common.delete': 'Supprimer',
    'common.edit': 'Modifier',
    'common.view': 'Voir',
    'common.refresh': 'Actualiser',
    'common.export': 'Exporter',
    'common.download': 'Télécharger',
    'common.search': 'Rechercher',
    'common.filter': 'Filtrer',

    // Dashboard
    'dashboard.title': 'Tableau de Bord',
    'dashboard.subtitle': 'Informations en temps réel pour votre plateforme vocale IA',
    'dashboard.totalCalls': 'Total des Appels',
    'dashboard.activeCalls': 'Appels Actifs',
    'dashboard.avgDuration': 'Durée Moyenne',
    'dashboard.revenue': 'Revenus',
    'dashboard.activeUsers': 'Utilisateurs Actifs',
    'dashboard.systemUptime': 'Temps de Fonctionnement',
    'dashboard.alerts': 'Alertes Actives',

    // Auth
    'auth.login': 'Se Connecter',
    'auth.register': "S'inscrire",
    'auth.email': 'Adresse Email',
    'auth.passcode': "Code d'Accès",
    'auth.firstName': 'Prénom',
    'auth.lastName': 'Nom',
    'auth.companyName': "Nom de l'Entreprise",
    'auth.signIn': 'Se Connecter',
    'auth.signUp': "S'inscrire",
    'auth.dontHaveAccount': "Vous n'avez pas de compte?",
    'auth.alreadyHaveAccount': 'Vous avez déjà un compte?'
  },

  de: {
    // Navigation
    'nav.overview': 'Übersicht',
    'nav.calls': 'Anrufe',
    'nav.analytics': 'Analytik',
    'nav.orders': 'Bestellungen',
    'nav.users': 'Benutzer',
    'nav.monitoring': 'Überwachung',
    'nav.billing': 'Abrechnung',
    'nav.settings': 'Einstellungen',

    // Common
    'common.loading': 'Laden...',
    'common.error': 'Fehler',
    'common.success': 'Erfolg',
    'common.save': 'Speichern',
    'common.cancel': 'Abbrechen',
    'common.delete': 'Löschen',
    'common.edit': 'Bearbeiten',
    'common.view': 'Ansehen',
    'common.refresh': 'Aktualisieren',
    'common.export': 'Exportieren',
    'common.download': 'Herunterladen',
    'common.search': 'Suchen',
    'common.filter': 'Filtern',

    // Dashboard
    'dashboard.title': 'Dashboard Übersicht',
    'dashboard.subtitle': 'Echtzeit-Einblicke für Ihre KI-Sprachplattform',
    'dashboard.totalCalls': 'Gesamte Anrufe',
    'dashboard.activeCalls': 'Aktive Anrufe',
    'dashboard.avgDuration': 'Durchschn. Dauer',
    'dashboard.revenue': 'Umsatz',
    'dashboard.activeUsers': 'Aktive Benutzer',
    'dashboard.systemUptime': 'System-Betriebszeit',
    'dashboard.alerts': 'Aktive Warnungen',

    // Auth
    'auth.login': 'Anmelden',
    'auth.register': 'Registrieren',
    'auth.email': 'E-Mail-Adresse',
    'auth.passcode': 'Zugangscode',
    'auth.firstName': 'Vorname',
    'auth.lastName': 'Nachname',
    'auth.companyName': 'Firmenname',
    'auth.signIn': 'Anmelden',
    'auth.signUp': 'Registrieren',
    'auth.dontHaveAccount': 'Haben Sie kein Konto?',
    'auth.alreadyHaveAccount': 'Haben Sie bereits ein Konto?'
  }
};

// Available languages
export const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
];

export function LanguageProvider({ children }) {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    // Load saved language from localStorage
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage && translations[savedLanguage]) {
      setCurrentLanguage(savedLanguage);
    } else {
      // Try to detect browser language
      const browserLanguage = navigator.language.split('-')[0];
      if (translations[browserLanguage]) {
        setCurrentLanguage(browserLanguage);
      }
    }
  }, []);

  const changeLanguage = (languageCode) => {
    if (translations[languageCode]) {
      setCurrentLanguage(languageCode);
      localStorage.setItem('language', languageCode);
    }
  };

  const t = (key, fallback = key) => {
    const translation = translations[currentLanguage]?.[key];
    return translation || translations['en']?.[key] || fallback;
  };

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    languages,
    isRTL: ['ar', 'he', 'fa'].includes(currentLanguage)
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;